import { KeyboardEventHandler, useEffect } from "react";
import clsx from "clsx";
import { BoldIcon, ListIcon } from "lucide-react";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Document } from "@tiptap/extension-document";
import { ListItem } from "@tiptap/extension-list-item";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";

// TODO [Low][LAB-331]: Integrate with Input component.

export function RichTextEditorInput({
  editorHeight,
  onChange,
  onKeyDown,
  value,
}: {
  editorHeight?: number | string;
  onChange: (value: string) => void;
  onKeyDown?: KeyboardEventHandler;
  value: string;
}) {
  const editor = useEditor({
    // NOTE: When adding or removing an extension, the `SanitizeHtmlService` needs
    // to be updated on the 🥑 side otherwise the editor may not work as expected.
    extensions: [Bold, BulletList, Document, ListItem, Paragraph, Text],
    onUpdate: ({ editor }) => {
      // TODO: Ensure no extra renders.
      if (editor.isEmpty) {
        onChange("");
      } else {
        onChange(editor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 border-b border-slate-200 p-2">
        <button
          onClick={() => editor?.chain().focus().toggleBold().run()}
          // Needed to avoid submitting the form.
          type="button"
        >
          <BoldIcon
            className={clsx(
              "h-4 w-4 text-slate-400",
              editor?.isActive("bold") && "text-slate-900",
            )}
          />
        </button>
        <button
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          // Needed to avoid submitting the form.
          type="button"
        >
          <ListIcon
            className={clsx(
              "h-5 w-5 text-slate-400",
              editor?.isActive("bulletList") && "text-slate-900",
            )}
          />
        </button>
      </div>
      <EditorContent
        className="overflow-y-auto"
        editor={editor}
        onKeyDown={onKeyDown}
        style={{ height: editorHeight }}
      />
    </div>
  );
}
